"use client";

/**
 * Currency display for React surfaces — USD base, EUR localized.
 *
 * Single import home for every webApp + the desktop (`@askfinz/shared-components/lib/currency`).
 * Pure math/format lives in `@askfinz/shared-code` (`format-currency.ts`); this file
 * adds the HYDRATION-SAFE React hooks. See that file for the USD-base decision.
 *
 * Hydration rule (project-wide, see CLAUDE.md): `resolveDisplayCurrency()` reads
 * `navigator`, which doesn't exist on the server — calling it during render would
 * make SSR markup ("$") differ from the client ("€") and trigger React #418
 * fleet-wide. So the hook returns the BASE currency (USD) on the server + first
 * client paint, then switches to the visitor's localized currency in a post-mount
 * effect. Never call `resolveDisplayCurrency()` directly in render.
 */

import { useEffect, useState } from "react";
import {
  formatMoney as _formatMoney,
  resolveDisplayCurrency,
  type DisplayCurrency,
} from "@askfinz/shared-code";

// Re-export the pure helpers so callers have one import site.
export {
  formatMoney,
  convertFromUsd,
  resolveDisplayCurrency,
  USD_TO_EUR,
  BASE_CURRENCY,
  type DisplayCurrency,
} from "@askfinz/shared-code";

/**
 * The display currency for this visitor — `"USD"` during SSR + first client paint
 * (so hydration matches), then the localized currency (EUR for eurozone visitors)
 * after mount. Optionally seed a `regionHint` (e.g. from a geoip cookie) to skip
 * the post-mount flip.
 */
export function useDisplayCurrency(regionHint?: string): DisplayCurrency {
  const [currency, setCurrency] = useState<DisplayCurrency>(
    regionHint ? resolveDisplayCurrency(regionHint) : "USD",
  );
  useEffect(() => {
    setCurrency(resolveDisplayCurrency(regionHint));
  }, [regionHint]);
  return currency;
}

/**
 * A formatter bound to the visitor's display currency: `const money = useMoney();
 * money(7.99)` → `"$7.99"` (US) / `"~€7.35"` (EU). All amounts passed in are the
 * BASE (USD) value. EUR is marked approximate ("~") since the real rate applies at
 * Stripe checkout (Adaptive Pricing).
 */
export function useMoney(
  regionHint?: string,
): (usd: number | null | undefined, opts?: { cents?: boolean }) => string {
  const currency = useDisplayCurrency(regionHint);
  return (usd, opts) =>
    _formatMoney(usd, currency, { ...opts, approx: currency === "EUR" });
}
